import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import {
  useUpdateEntity,
  useUpdatePropNew,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  AlignStyle,
  FontSize,
} from 'client/components/core/Sidebar/components/Settings/styles'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { SearchResultsInterface } from 'common/types/entities/search-results-interface'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { MarginGroup } from 'src/editor/shared/settings/groups/margin'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import DeviceAppearanceNew from '../../options/DeviceAppearance/DeviceAppearanceNew'
import Border from '../../styles/Border'

interface SearchResultsSettingsProps {
  entity: SearchResultsInterface
  defaultEntity?: SearchResultsInterface
}

export const SearchResultsSettings = ({
  entity,
  defaultEntity,
}: SearchResultsSettingsProps) => {
  const updateEntity = useUpdateEntity<SearchResultsInterface>()
  const updateProp = useUpdatePropNew(entity)
  const { isMobile } = useDeviceModeContext()

  return (
    <>
      <RangeElement
        label="entity_settings.search_results.maximum_results_per_page"
        onChange={updateProp('maxItems')}
        value={entity.maxItems}
        allowNegative={false}
      />
      {/* card settings */}
      <GroupTitle>entity_settings.search_results.card_settings</GroupTitle>
      <ColorPickerControl
        update={updateProp('card', 'backgroundColor')}
        mobileUpdate={updateProp('card', 'mobileBackgroundColor')}
        color={entity.card?.backgroundColor}
        mobileColor={entity.card?.mobileBackgroundColor}
        label="settings_styles.background_color.label"
        isClearable
      />
      <Border
        showGroupTitle={false}
        update={updateProp('card', 'border')}
        mobileUpdate={updateProp('card', 'mobileBorder')}
        border={entity.card?.border}
        mobileBorder={entity.card?.mobileBorder}
        defaultBorder={defaultEntity?.card?.border}
        mobileDefaultBorder={defaultEntity?.card?.mobileBorder}
      />
      <PaddingGroup
        padding={isMobile ? entity.card?.mobilePadding : entity.card?.padding}
        update={updateProp('card', isMobile ? 'mobilePadding' : 'padding')}
        label="settings_styles.padding.label"
      />
      {/* total results text */}
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                ['total', 'fontFamily'],
                ['total', 'fontSize'],
                ['total', 'fontWeight'],
                ['total', 'fontStyle'],
                ['total', 'fontFileId'],
                ['total', 'textAlign'],
                ['total', 'lineHeight'],
                ['total', 'color'],
              ]}
              mobileKeys={[
                ['total', 'mobileFontFamily'],
                ['total', 'mobileFontSize'],
                ['total', 'mobileFontWeight'],
                ['total', 'mobileFontStyle'],
                ['total', 'mobileFontFileId'],
                ['total', 'mobileTextAlign'],
                ['total', 'mobileLineHeight'],
                ['total', 'mobileColor'],
              ]}
            />
          )
        }
      >
        entity_settings.search_results.total_results_text
      </GroupTitle>
      <FontSize
        update={updateProp('total', 'fontSize')}
        mobileUpdate={updateProp('total', 'mobileFontSize')}
        fontSize={entity.total.fontSize}
        mobileFontSize={entity.total.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.total.fontSize}
        mobileFontSize={entity.total.mobileFontSize}
        update={updateProp('total', 'lineHeight')}
        mobileUpdate={updateProp('total', 'mobileLineHeight')}
        lineHeight={entity.total.lineHeight}
        mobileLineHeight={entity.total.mobileLineHeight}
      />
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="total"
      />
      <AlignStyle
        align={isMobile ? entity.total.mobileTextAlign : entity.total.textAlign}
        update={updateProp('total', isMobile ? 'mobileTextAlign' : 'textAlign')}
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('total', 'color')}
        mobileUpdate={updateProp('total', 'mobileColor')}
        color={entity.total.color}
        mobileColor={entity.total.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.total.mobileMargin : entity.total.margin}
        update={updateProp('total', isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* card title */}
      <GroupTitle>entity_settings.search_results.card_title</GroupTitle>
      <FontSize
        update={updateProp('title', 'fontSize')}
        mobileUpdate={updateProp('title', 'mobileFontSize')}
        fontSize={entity.title.fontSize}
        mobileFontSize={entity.title.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.title.fontSize}
        mobileFontSize={entity.title.mobileFontSize}
        update={updateProp('title', 'lineHeight')}
        mobileUpdate={updateProp('title', 'mobileLineHeight')}
        lineHeight={entity.title.lineHeight}
        mobileLineHeight={entity.title.mobileLineHeight}
      />
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="title"
      />
      <AlignStyle
        align={isMobile ? entity.title.mobileTextAlign : entity.title.textAlign}
        update={updateProp('title', isMobile ? 'mobileTextAlign' : 'textAlign')}
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('title', 'color')}
        mobileUpdate={updateProp('title', 'mobileColor')}
        color={entity.title.color}
        mobileColor={entity.title.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.title.mobileMargin : entity.title.margin}
        update={updateProp('title', isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* card text */}
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                ['text', 'fontFamily'],
                ['text', 'fontSize'],
                ['text', 'fontWeight'],
                ['text', 'fontStyle'],
                ['text', 'fontFileId'],
                ['text', 'textAlign'],
                ['text', 'lineHeight'],
                ['text', 'color'],
              ]}
              mobileKeys={[
                ['text', 'mobileFontFamily'],
                ['text', 'mobileFontSize'],
                ['text', 'mobileFontWeight'],
                ['text', 'mobileFontStyle'],
                ['text', 'mobileFontFileId'],
                ['text', 'mobileTextAlign'],
                ['text', 'mobileLineHeight'],
                ['text', 'mobileColor'],
              ]}
            />
          )
        }
      >
        entity_settings.search_results.card_text
      </GroupTitle>
      <FontSize
        update={updateProp('text', 'fontSize')}
        mobileUpdate={updateProp('text', 'mobileFontSize')}
        fontSize={entity.text.fontSize}
        mobileFontSize={entity.text.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.text.fontSize}
        mobileFontSize={entity.text.mobileFontSize}
        update={updateProp('text', 'lineHeight')}
        mobileUpdate={updateProp('text', 'mobileLineHeight')}
        lineHeight={entity.text.lineHeight}
        mobileLineHeight={entity.text.mobileLineHeight}
      />
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="text"
      />
      <AlignStyle
        align={isMobile ? entity.text.mobileTextAlign : entity.text.textAlign}
        update={updateProp('text', isMobile ? 'mobileTextAlign' : 'textAlign')}
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('text', 'color')}
        mobileUpdate={updateProp('text', 'mobileColor')}
        color={entity.text.color}
        mobileColor={entity.text.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.text.mobileMargin : entity.text.margin}
        update={updateProp('text', isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* card date */}
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                ['date', 'fontFamily'],
                ['date', 'fontSize'],
                ['date', 'fontWeight'],
                ['date', 'fontStyle'],
                ['date', 'fontFileId'],
                ['date', 'textAlign'],
                ['date', 'lineHeight'],
                ['date', 'color'],
              ]}
              mobileKeys={[
                ['date', 'mobileFontFamily'],
                ['date', 'mobileFontSize'],
                ['date', 'mobileFontWeight'],
                ['date', 'mobileFontStyle'],
                ['date', 'mobileFontFileId'],
                ['date', 'mobileTextAlign'],
                ['date', 'mobileLineHeight'],
                ['date', 'mobileColor'],
              ]}
            />
          )
        }
      >
        entity_settings.search_results.card_date
      </GroupTitle>
      <FontSize
        update={updateProp('date', 'fontSize')}
        mobileUpdate={updateProp('date', 'mobileFontSize')}
        fontSize={entity.date.fontSize}
        mobileFontSize={entity.date.mobileFontSize}
      />
      <TextLineHeight
        fontSize={entity.date.fontSize}
        mobileFontSize={entity.date.mobileFontSize}
        update={updateProp('date', 'lineHeight')}
        mobileUpdate={updateProp('date', 'mobileLineHeight')}
        lineHeight={entity.date.lineHeight}
        mobileLineHeight={entity.date.mobileLineHeight}
      />
      <TextFontUpdater<SearchResultsInterface>
        entity={entity}
        update={updateEntity}
        nestedKey="date"
      />
      <AlignStyle
        align={isMobile ? entity.date.mobileTextAlign : entity.date.textAlign}
        update={updateProp('date', isMobile ? 'mobileTextAlign' : 'textAlign')}
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('date', 'color')}
        mobileUpdate={updateProp('date', 'mobileColor')}
        color={entity.date.color}
        mobileColor={entity.date.mobileColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.date.mobileMargin : entity.date.margin}
        update={updateProp('date', isMobile ? 'mobileMargin' : 'margin')}
      />
      {/* pagination */}
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                ['pagination', 'activeColor'],
                ['pagination', 'activeBackgroundColor'],
                ['pagination', 'inactiveColor'],
                ['pagination', 'inactiveBackgroundColor'],
              ]}
              mobileKeys={[
                ['pagination', 'mobileActiveColor'],
                ['pagination', 'mobileActiveBackgroundColor'],
                ['pagination', 'mobileInactiveColor'],
                ['pagination', 'mobileInactiveBackgroundColor'],
              ]}
            />
          )
        }
      >
        entity_settings.search_results.pagination
      </GroupTitle>
      <ColorPickerControl
        update={updateProp('pagination', 'activeColor')}
        mobileUpdate={updateProp('pagination', 'mobileActiveColor')}
        color={entity.pagination.activeColor}
        mobileColor={entity.pagination.mobileActiveColor}
        label="entity_settings.blog_post_listing.pagination.active_color"
      />
      <ColorPickerControl
        update={updateProp('pagination', 'activeBackgroundColor')}
        mobileUpdate={updateProp('pagination', 'mobileActiveBackgroundColor')}
        color={entity.pagination.activeBackgroundColor}
        mobileColor={entity.pagination.mobileActiveBackgroundColor}
        label="entity_settings.blog_post_listing.pagination.active_background_color"
      />
      <ColorPickerControl
        update={updateProp('pagination', 'inactiveColor')}
        mobileUpdate={updateProp('pagination', 'mobileInactiveColor')}
        color={entity.pagination.inactiveColor}
        mobileColor={entity.pagination.mobileInactiveColor}
        label="entity_settings.blog_post_listing.pagination.non_active_color"
      />
      <ColorPickerControl
        update={updateProp('pagination', 'inactiveBackgroundColor')}
        mobileUpdate={updateProp('pagination', 'mobileInactiveBackgroundColor')}
        color={entity.pagination.inactiveBackgroundColor}
        mobileColor={entity.pagination.mobileInactiveBackgroundColor}
        label="entity_settings.blog_post_listing.pagination.non_active_background_color"
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <DeviceAppearanceNew entity={entity} />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
